<template>
  <v-dialog
    v-model="dialog"
    max-width="1300"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card>
      <!--CLOSE-->
      <v-card-actions class="redColor text-white">
        <v-spacer />
        <v-btn
          class="ma-2"
          variant="outlined"
          icon
          color="white"
          @click="close"
        >
          <v-icon x-large> mdi-close </v-icon>
        </v-btn>
      </v-card-actions>
      <v-card-text>
        <!--LOADING-->
        <v-row v-if="loading" align-center justify-center>
          <v-progress-circular indeterminate size="64" color="red" />
        </v-row>
        <!--CODES-->
        <v-row>
          <v-col
            v-for="(data, index) in shelves"
            :key="index"
            xs="12"
            sm="4"
            md="3"
            lg="2"
          >
            <v-card flat class="pb-4">
              <v-row class="justify-center d-flex flex-column">
                <v-col pa-2 xs="12">
                  <!--QR CODE-->
                  <qr-code
                    class="d-flex justify-center"
                    :size="150"
                    :text="data._id"
                  />
                  <!--DETAILS-->
                  <div class="text-center">
                    <div>{{ repository }}</div>
                    <span
                      >{{ data.room_name }} - {{ data.location_code }}:
                    </span>
                    <span>{{ data.x }},{{ data.y }}</span>
                  </div>
                  <h3 class="text-center">
                    <v-btn color="black" variant="outlined" small>
                      {{ data._id.slice(data._id.length - 6, data._id.length) }}
                    </v-btn>
                  </h3>
                  <!--BUTTONS-->
                  <v-row class="justify-center mt-1">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-btn
                          variant="text"
                          icon
                          v-on="on"
                          @click="printQrCode(index, data)"
                        >
                          <v-icon>
                            {{
                              data.printed ? "mdi-printer-off" : "mdi-printer"
                            }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span v-if="data.printed">
                        <v-row align-center>
                          <v-icon> mdi-printer-off</v-icon>
                          &nbsp;
                          {{ $t("printed_alert") }}
                        </v-row>
                      </span>
                    </v-tooltip>
                    <v-btn variant="text" icon @click="showParts(data)">
                      <v-icon>mdi-format-list-bulleted</v-icon>
                    </v-btn>
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-btn
                          variant="text"
                          icon
                          :loading="loadingPrintParts[data._id]"
                          v-on="on"
                          @click="printPartsList(data)"
                        >
                          <v-icon>mdi-printer-eye</v-icon>

                          <v-icon size="x-small" style="margin-left: -2px"
                            >mdi-format-list-checkbox</v-icon
                          >
                        </v-btn>
                      </template>
                      <span>{{ $t("repositories.print_parts_list") }}</span>
                    </v-tooltip>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <!--PRINT-->
      <v-card-actions>
        <v-row class="justify-end">
          <v-spacer />
          <v-col xs="2" md="2">
            <v-btn
              variant="outlined"
              :disabled="shelves.length < 1"
              color="primary"
              @click="printShelves()"
            >
              {{ $t("repositories.print_shelf") }}
              <v-icon end>mdi-printer</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VueQrCode from "vue-qr-generator";
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import moment from "moment";
import QrCode from "qrcode";
import { Printd } from "printd";

const ROBOTO_FONT =
  "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700|Material+Icons";

const SHELF_QR_SIZE = "70px";

const shelfSinglePrintCss = [
  `
  @media print {
    @page {
      size: 4cm 3cm;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      height: auto;
      overflow: hidden;
    }
    #content {
      padding: 2px 0;
      display: grid;
      gap: 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    #qrImage {
      margin: 0 auto;
      width: ${SHELF_QR_SIZE} !important;
      height: ${SHELF_QR_SIZE} !important;
    }
    #locationText {
      font-family: "Roboto";
      font-size: 7px;
      line-height: 1;
      text-align: center;
      font-weight: bold;
      margin: 0 auto;
    }
    #idText {
      font-family: "Roboto";
      font-size: 14px;
      line-height: 1;
      text-align: center;
      font-weight: bold;
      text-transform: uppercase;
      margin: 0 auto;
    }
    #repositoryText {
      font-family: "Roboto";
      font-size: 7px;
      line-height: 1;
      text-align: center;
      font-weight: bold;
      margin: 0 auto;
      padding-top: 2px;
    }
  }
  `,
  ROBOTO_FONT,
];

const shelfBulkPrintCss = [
  `
@media print {
  @page {
    size: 4cm 3cm;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    height: auto;
  }
  .part {
    text-align: center;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
  }
  .qrImage {
    object-fit: contain;
    display: block;
    margin: 0 auto;
    width: ${SHELF_QR_SIZE} !important;
    height: ${SHELF_QR_SIZE} !important;
  }
  .locationText {
    font-family: "Roboto";
    font-size: 1.6mm;
    line-height: 1;
    font-weight: bold;
    text-align: center;
  }
  .idText {
    font-family: "Roboto";
    font-size: 2.8mm;
    line-height: 1;
    font-weight: bold;
    text-align: center;
    text-transform: uppercase;
  }
  .repositoryText {
    font-family: "Roboto";
    font-size: 1.6mm;
    line-height: 1;
    font-weight: bold;
    margin-top: 0.5mm;
    text-align: center;
  }
}
`,
];
const lgcss = `@media print{
  @page{
    size: A4;
    margin: 4mm;
  }
  .section-container {
    display: flex;
    flex-wrap: wrap;
    margin: 0;
    page-break-inside: avoid;
    align-content: flex-start;
  }
  #container{
    display: block;
  }
  .wrapper{
    margin: 1px 2px;
    width: 140px;
    text-align: center;
    page-break-inside: avoid;
  }
  h2 {
    font-size: 16px;
    margin: 4px 0;
    border-bottom: 1px solid #000;
    width: 100%;
  }
}`;

const partsListCss = `
  body{
    height: auto;
    width: 100%;
    margin: 0;
    padding: 0;
    font: 10px "Lucida Grande", Helvetica, Arial, sans-serif;
  }
  #caption{
    font-size: 10px;
    text-align: center;
    word-wrap: break-word;
    width: 100%;
  }`;
const rowcss = `
@media print {
  .print-rows {
    display: block !important;
  }

  .print-rows .wrapper {
    display: block;
    width: 100%;
    margin: 6px 0;
    page-break-inside: avoid;
  }
}
`;
function formatDate(date, language = "en") {
  if (!date) return "";
  return moment(date).locale(language).format("DD MMMM YYYY");
}
function generatePartQrComponent(item, language) {
  return new Promise((resolve) => {
    const qrContent = item.old_id ? item.old_id.slice(-6) : item._id.slice(-6);

    let titleText = "";
    if (
      item.infoCar &&
      item.infoCar.description &&
      item.infoCar.description.descriptionId &&
      item.infoCar.description.descriptionId.title
    ) {
      titleText = item.infoCar.description.descriptionId.title[language] || "";
    }
    let date = formatDate(item.date_created, language);

    const caption = document.createElement("div");
    caption.className = "caption";
    caption.textContent = `${qrContent} - ${titleText} - ${date ? date.toUpperCase() : ""}`;

    const wrapper = document.createElement("div");
    wrapper.className = "wrapper";
    wrapper.appendChild(caption);

    resolve(wrapper);
  });
}

function generateSingleShelfPrintContent(repo, shelf) {
  return new Promise((resolve, reject) => {
    QrCode.toDataURL(shelf._id, (error, url) => {
      if (error) return reject(error);

      const locationText = document.createElement("span");
      locationText.textContent = `${shelf.room_name}-${shelf.location_code}:${shelf.x},${shelf.y}`;
      locationText.setAttribute("id", "locationText");

      const idComp = document.createElement("span");
      idComp.textContent = shelf._id.slice(-6);
      idComp.setAttribute("id", "idText");

      const qrImage = document.createElement("img");
      qrImage.setAttribute("id", "qrImage");
      qrImage.setAttribute("src", url);
      qrImage.style.width = SHELF_QR_SIZE;
      qrImage.style.height = SHELF_QR_SIZE;

      const repositoryText = document.createElement("span");
      repositoryText.textContent = repo;
      repositoryText.setAttribute("id", "repositoryText");

      const content = document.createElement("div");
      content.setAttribute("id", "content");
      content.appendChild(locationText);
      content.appendChild(idComp);
      content.appendChild(qrImage);
      content.appendChild(repositoryText);

      resolve(content);
    });
  });
}

async function generateBulkShelfPrintContent(repo, shelves) {
  const content = document.createElement("div");
  content.setAttribute("id", "content");

  const qrPromises = shelves.map(
    (shelf) =>
      new Promise((resolve, reject) => {
        QrCode.toDataURL(shelf._id, (error, url) => {
          if (error) return reject(error);
          resolve({ shelf, url });
        });
      }),
  );

  const qrResults = await Promise.all(qrPromises);

  qrResults.forEach(({ shelf, url }) => {
    const partComp = document.createElement("div");
    partComp.className = "part";

    const locationText = document.createElement("div");
    locationText.textContent = `${shelf.room_name}-${shelf.location_code}:${shelf.x},${shelf.y}`;
    locationText.className = "locationText";

    const idComp = document.createElement("div");
    idComp.textContent = shelf._id.slice(-6);
    idComp.className = "idText";

    const qrImage = document.createElement("img");
    qrImage.src = url;
    qrImage.className = "qrImage";
    qrImage.style.width = SHELF_QR_SIZE;
    qrImage.style.height = SHELF_QR_SIZE;

    const repositoryText = document.createElement("div");
    repositoryText.textContent = repo;
    repositoryText.className = "repositoryText";

    partComp.appendChild(locationText);
    partComp.appendChild(idComp);
    partComp.appendChild(qrImage);
    partComp.appendChild(repositoryText);

    content.appendChild(partComp);
  });

  return content;
}

export default {
  components: {
    "qr-code": VueQrCode,
  },
  data: () => ({
    dialog: false,
    loading: true,
    repository: null,
    room: {},
    shelves: [],
    loadingPrintParts: {},
  }),
  watch: {
    dialog(value) {
      if (!value) this.close();
    },
  },
  methods: {
    open(data) {
      this.dialog = true;
      this.getShelves(data);
    },
    close() {
      this.dialog = false;
      this.repository = null;
      this.room = {};
      this.shelves = [];
      this.$emit("close");
    },
    getShelves(data) {
      this.loading = true;
      getRequest(
        "/repository/shelves",
        `repository_id=${data.repositoryId}&room_id=${data.roomId}${data.locationId ? `&location_id=${data.locationId}` : ""}`,
      )
        .then((response) => {
          this.repository = response.repository;
          this.room = response.room;
          this.shelves = data.locationId
            ? response.room.locations.shelves
            : response.room.locations.reduce(
                (acc, val) => acc.concat(val.shelves),
                [],
              );
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    async printQrCode(index, shelf) {
      try {
        const content = await generateSingleShelfPrintContent(
          this.repository,
          shelf,
        );
        const d = new Printd();
        d.print(content, shelfSinglePrintCss, "", (callback) => {
          const { iframe, launchPrint } = callback;
          const { contentWindow } = iframe;
          contentWindow.addEventListener("afterprint", () => {
            postRequest.object("/print", { type: "shelves", id: shelf._id });
          });
          launchPrint();
        });
      } catch {}
    },
    async printShelves() {
      try {
        const content = await generateBulkShelfPrintContent(
          this.repository,
          this.shelves,
        );
        const d = new Printd();
        d.print(content, shelfBulkPrintCss, "", (callback) => {
          const { iframe, launchPrint } = callback;
          iframe.contentWindow.addEventListener("afterprint", () => {
            postRequest.object("/print", {
              type: "shelves",
              ids: this.shelves.map((s) => s._id),
            });
          });
          launchPrint();
        });
      } catch {}
    },
    async printPartsList(shelf, index) {
      this.loadingPrintParts[shelf._id] = true;

      try {
        const response = await getRequest(
          "/repository/parts",
          `id=${shelf._id}`,
        );

        const parts = response.parts || [];
        const temporaryParts = response.temporaryParts || [];

        if (!parts.length && !temporaryParts.length) {
          this.loadingPrintParts[shelf._id] = false;

          return;
        }

        const container = document.createElement("div");
        container.setAttribute("id", "container");

        /* ================= PARTS ================= */
        if (parts.length) {
          const partsHeader = document.createElement("h2");
          partsHeader.textContent = this.$t("parts.inventory.label");
          container.appendChild(partsHeader);

          const partsContainer = document.createElement("div");
          partsContainer.setAttribute("class", "section-container print-rows");

          const partComponents = await Promise.all(
            parts.map((part) =>
              generatePartQrComponent(part, this.$store.getters.language),
            ),
          );

          partComponents.forEach((c) => partsContainer.appendChild(c));
          container.appendChild(partsContainer);
        }

        /* ============ TEMPORARY PARTS ============ */
        if (temporaryParts.length) {
          const tempHeader = document.createElement("h2");
          tempHeader.textContent = this.$t("parts.insert_parts.label");
          container.appendChild(tempHeader);

          const tempContainer = document.createElement("div");
          tempContainer.setAttribute("class", "section-container print-rows");

          const tempComponents = await Promise.all(
            temporaryParts.map((part) =>
              generatePartQrComponent(part, this.$store.getters.language),
            ),
          );

          tempComponents.forEach((c) => tempContainer.appendChild(c));
          container.appendChild(tempContainer);
        }

        const d = new Printd();
        d.print(container, [lgcss, partsListCss, rowcss]);
        this.loadingPrintParts[shelf._id] = false;
      } catch (e) {
        console.error(e);
        this.loadingPrintParts[shelf._id] = false;
      }
    },

    showParts(data) {
      this.$emit("parts", data);
    },
  },
};
</script>
<style scoped>
.wrapper {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  page-break-inside: avoid;
  text-align: left;
}

.caption {
  font-size: 14px;
  line-height: 1.4;
}
</style>
